import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LogOut,
  UserRound,
  Inbox,
  Droplets,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Pause,
  CloudUpload,
  NotebookPen,
  Presentation,
  History,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  DoorOpen,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { XiaBar } from '../components/ui/XiaBar';
import { IndicadorConexion } from '../components/IndicadorConexion';
import { LevantaEvidencia } from '../components/LevantaEvidencia';
import { primitivaDe } from '../components/primitivas/registro';
import { CapturaNoSoportada } from '../components/primitivas/CapturaNoSoportada';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { useSesionActiva } from '../hooks/useSesionActiva';
import { useSalaViva, useColaPendientes } from '../hooks/useSalaViva';
import { useTiempoServidor } from '../components/Temporizador';
import { enviarAporte, type ResultadoEnvio } from '../lib/colaOffline';
import { ApiError } from '../lib/api';
import { unirmeASala } from '../lib/sala';
import { forzarActualizacion } from '../lib/actualizar';
import { ColaAudio } from '../components/ColaAudio';
import {
  sesionApi,
  sesionKeys,
  resolverCaptura,
  resumirAporte,
  type ActividadDetalle,
  type Aporte,
  type AporteTipo,
  type DiarioActividad,
  type ProcesoResumen,
  type Transcripcion,
} from '../lib/sesion';
import { leerSala, fechaSalaHumana } from '../lib/sala';
import { BotonTema } from '../components/ui/BotonTema';
import { EsqueletoTarjeta } from '../components/ui/Esqueleto';

/**
 * Bolsillo (/app) — superficie del participante. Mobile-first real (380px):
 * UNA acción por pantalla. Muestra la actividad ABIERTA del proceso y, según
 * la captura de la herramienta, pide texto, voto o posición. Envía el aporte
 * (POST, INSERT-only C1) y confirma en humano. Poll suave a la espera de la
 * siguiente actividad. Estado vacío que enseña con ejemplo fantasma GT.
 */
export function BolsilloPage() {
  useSurfaceTheme('bolsillo', 'light');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const { sesionId, procesoActivo, cargando: cargandoSesion } = useSesionActiva();

  // A qué sala se entró: se muestra SIEMPRE, no solo en la bienvenida. Aportar
  // en la sala equivocada tiene que ser imposible de no notar.
  const salaEntrada = leerSala();
  const cuandoSala = fechaSalaHumana(salaEntrada?.sesion_fecha ?? null);

  // Confirmación de entrada por sala (?bienvenida=1) y vista «Mis participaciones».
  const [searchParams, setSearchParams] = useSearchParams();
  const bienvenida = searchParams.get('bienvenida') === '1';
  const [verParticipaciones, setVerParticipaciones] = useState(false);

  // Canal en vivo (WS): la apertura/pausa de la actividad llega en < 1 s.
  const sala = useSalaViva(sesionId);
  const pendientes = useColaPendientes();
  const poll = sala.enVivo ? 20_000 : 3000;

  // Agenda: buscamos la actividad EN CURSO (abierta o pausada).
  const sesion = useQuery({
    queryKey: sesionId ? sesionKeys.sesion(sesionId) : ['sesion', 'nada'],
    queryFn: () => sesionApi.sesion(sesionId!),
    enabled: Boolean(sesionId),
    refetchInterval: poll,
  });

  const focoResumen =
    sesion.data?.actividades.find((a) => a.estado === 'abierta') ??
    sesion.data?.actividades.find((a) => a.estado === 'pausada') ??
    null;

  // Detalle de la actividad en curso (captura + XIA).
  const actividad = useQuery({
    queryKey: focoResumen ? sesionKeys.actividad(focoResumen.id) : ['actividad', 'nada'],
    queryFn: () => sesionApi.actividad(focoResumen!.id),
    enabled: Boolean(focoResumen),
    refetchInterval: poll,
  });

  const act =
    actividad.data &&
    (actividad.data.estado === 'abierta' || actividad.data.estado === 'pausada')
      ? actividad.data
      : null;

  // Diario de campo (M2): herramienta ENTRE-SESIONES. Solo se consulta cuando
  // NO hay actividad en curso — con actividad viva, lo vivo manda.
  const diario = useQuery({
    queryKey: procesoActivo ? sesionKeys.diario(procesoActivo.id) : ['diario', 'nada'],
    queryFn: () => sesionApi.diario(procesoActivo!.id),
    enabled: Boolean(procesoActivo) && !focoResumen,
    refetchInterval: 20_000,
  });
  const entradasDiario = !focoResumen && diario.data ? diario.data : [];

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  function continuarDesdeBienvenida() {
    const next = new URLSearchParams(searchParams);
    next.delete('bienvenida');
    setSearchParams(next, { replace: true });
  }

  // Confirmación al entrar por sala: una sola pantalla, antes que todo.
  if (bienvenida) {
    return (
      <Bienvenida
        proceso={procesoActivo}
        cuando={cuandoSala}
        cargando={cargandoSesion}
        nombre={user?.nombre ?? null}
        onContinuar={continuarDesdeBienvenida}
      />
    );
  }

  if (verParticipaciones) {
    return <MisParticipaciones onVolver={() => setVerParticipaciones(false)} />;
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-pocket flex-col bg-surface-base">
      <header className="flex items-center justify-between px-4 pb-2 pt-4">
        <img src="/brand/logopositivo.png" alt="ÁGORA" className="h-7 w-auto" />
        <div className="flex items-center gap-3">
          {sesionId && <IndicadorConexion estado={sala.estadoConexion} pendientes={pendientes} />}
          {/* «No veo lo que debería ver»: vacía la caché del service worker y
              recarga contra el servidor. No toca la cola de audios ni la de
              texto — lo pendiente de enviar sobrevive. */}
          <button
            type="button"
            onClick={() => void forzarActualizacion()}
            className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
            aria-label="Actualizar: volver a cargar desde el servidor"
            title="Actualizar desde el servidor"
          >
            <RefreshCw className="h-5 w-5" aria-hidden="true" />
          </button>
          <BotonTema />
          <button
            type="button"
            onClick={() => navigate('/cuenta')}
            className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
            aria-label="Mi cuenta"
            title="Mi cuenta: perfil y plan"
          >
            <UserRound className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setVerParticipaciones(true)}
            className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
            aria-label="Mis participaciones"
          >
            <History className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* En qué sala estás, siempre a la vista: aportar en la sala equivocada
          deja de ser algo que pasa en silencio. */}
      {procesoActivo && (
        <p className="truncate px-4 pb-2 font-sans text-xs text-content-muted">
          Estás en{' '}
          <span className="font-semibold text-content">{procesoActivo.nombre}</span>
          {cuandoSala ? ` · ${cuandoSala}` : ''}
        </p>
      )}

      <ColaAudio />

      {!procesoActivo && !cargandoSesion ? (
        <SinSala />
      ) : act ? (
        <ActividadAbierta
          key={act.id}
          actividad={act}
          nombre={user?.nombre ?? null}
          offsetMs={sala.offsetServidorMs}
          transcripciones={sala.transcripciones}
          onEnviado={() => {
            qc.invalidateQueries({ queryKey: sesionKeys.actividad(act.id) });
            qc.invalidateQueries({ queryKey: ['aportes', act.id, 'mios'] });
          }}
        />
      ) : entradasDiario.length > 0 ? (
        <DiarioDeCampo
          entradas={entradasDiario}
          nombre={user?.nombre ?? null}
          offsetMs={sala.offsetServidorMs}
          transcripciones={sala.transcripciones}
        />
      ) : (
        <Espera cargando={cargandoSesion || sesion.isLoading} nombre={user?.nombre ?? null} />
      )}
    </div>
  );
}

/* ------------------------- Actividad abierta --------------------------- */

function ActividadAbierta({
  actividad,
  nombre,
  offsetMs,
  transcripciones,
  onEnviado,
}: {
  actividad: ActividadDetalle;
  nombre: string | null;
  offsetMs: number;
  transcripciones: Record<string, Transcripcion>;
  onEnviado: () => void;
}) {
  // Registro de primitivas (D5b): la página no ramifica por tipo de captura.
  // Un tipo fuera del enum -> estado visible "no soportada" (D5d), nunca blanco.
  const res = resolverCaptura(actividad.herramienta);
  const captura = res.soportada ? res.captura : null;
  const primitiva = captura ? primitivaDe(captura.tipo) : null;
  // Momento informativo (cápsula/lectura): sin captura DELIBERADAMENTE — el
  // protagonismo lo tiene la proyección, pero la evidencia rica sí queda.
  const informativa = !res.soportada && res.informativa === true;
  const tiempo = useTiempoServidor({
    abiertaAt: actividad.abierta_at,
    duracionMin: actividad.duracion_min,
    pausaAcumuladoS: actividad.pausa_acumulado_s,
    estado: actividad.estado,
    offsetMs,
  });
  const pausada = actividad.estado === 'pausada';

  const [resultado, setResultado] = useState<ResultadoEnvio['estado'] | null>(null);

  // El eco de lo tuyo: MIS aportes de esta actividad (mios=true). Alimenta a la
  // primitiva (fichas gastadas, mi marca en el plano) y a la lista de abajo.
  const mios = useQuery({
    queryKey: ['aportes', actividad.id, 'mios'],
    queryFn: () => sesionApi.aportes(actividad.id, undefined, true),
    enabled: actividad.estado === 'abierta' || actividad.estado === 'pausada',
    // Con panel de respuesta en vivo (Mago de Oz), el diálogo debe fluir.
    refetchInterval: 5000,
  });
  const todosMios = mios.data?.aportes ?? [];
  // Respuestas EN VIVO del facilitador a mis aportes (Mago de Oz, Oleada 4):
  // viajan por ?mios=true pero no son «mis aportes» (ni cuentan como tales).
  const respuestas = todosMios.filter(
    (a) => a.tipo === 'conexion' && a.payload.curaduria && a.payload.accion === 'responder',
  );
  const misAportes = todosMios.filter(
    (a) => !(a.tipo === 'conexion' && a.payload.curaduria),
  );

  // Modo RÁFAGA (texto sin secciones): varios aportes seguidos sin pasar por
  // la confirmación de pantalla completa — el formulario queda, las casillas
  // elegidas también, y solo se limpia el texto (selloEnvio).
  const rafaga = captura?.tipo === 'texto' && captura.secciones.length === 0;
  const [selloEnvio, setSelloEnvio] = useState(0);
  const [estadoRafaga, setEstadoRafaga] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (cuerpo: { tipo: AporteTipo; payload: Record<string, unknown> }) =>
      enviarAporte(actividad.id, cuerpo.tipo, cuerpo.payload),
    onSuccess: (r) => {
      if (rafaga) {
        setSelloEnvio((s) => s + 1);
        setEstadoRafaga(r.estado);
      } else {
        setResultado(r.estado);
      }
      onEnviado();
    },
  });

  function enviar(payload: Record<string, unknown>) {
    if (pausada || !captura) return;
    mut.mutate({ tipo: captura.tipo, payload });
  }

  function otro() {
    setResultado(null);
    mut.reset();
  }

  const errorMsg = (mut.error as ApiError | null)?.detail ?? null;

  return (
    <>
      <div className="px-4">
        <XiaBar
          que={actividad.herramienta.xia.que}
          porQue={actividad.herramienta.xia.por_que}
          hacia={actividad.herramienta.xia.hacia}
          tiempoRestante={tiempo.texto}
        />
      </div>

      <main className="flex flex-1 flex-col px-4 py-5">
        {resultado && primitiva ? (
          <Confirmacion
            otraVez={primitiva.otraVez}
            enCola={resultado === 'en_cola'}
            cuenta={misAportes.length}
            onOtro={otro}
          />
        ) : (
          <>
            <h1 className="font-display text-xl font-semibold text-content">
              {actividad.titulo ?? actividad.herramienta.nombre}
            </h1>
            {captura && primitiva && (
              <p className="mt-1 font-sans text-sm text-content-muted">
                {captura.ayuda ?? primitiva.instruccion}
              </p>
            )}

            {pausada && (
              <p
                role="status"
                className="mt-3 flex items-center gap-2 rounded-md border border-warning px-3 py-2 font-sans text-sm text-warning"
              >
                <Pause className="h-4 w-4 shrink-0" aria-hidden="true" />
                El facilitador puso la actividad en pausa. Esperá un momento para
                enviar tu aporte.
              </p>
            )}

            {errorMsg && (
              <p
                role="alert"
                className="mt-3 flex items-center gap-1.5 font-sans text-sm text-error"
              >
                <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {errorMsg}
              </p>
            )}

            <div className="mt-5 flex-1">
              {captura && primitiva ? (
                <>
                  <primitiva.Captura
                    captura={captura}
                    deshabilitado={pausada}
                    enviando={mut.isPending}
                    onEnviar={enviar}
                    misAportes={misAportes}
                    ronda={actividad.ronda ?? null}
                    actividadId={actividad.id}
                    selloEnvio={selloEnvio}
                  />
                  {/* Nota de la ráfaga: confirma sin sacar del formulario. */}
                  {rafaga && estadoRafaga && (
                    <p
                      className={`mt-3 flex items-center gap-1.5 font-sans text-sm font-semibold ${
                        estadoRafaga === 'en_cola' ? 'text-warning' : 'text-success'
                      }`}
                      aria-live="polite"
                    >
                      {estadoRafaga === 'en_cola' ? (
                        <CloudUpload className="h-4 w-4 shrink-0" aria-hidden="true" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                      )}
                      {estadoRafaga === 'en_cola'
                        ? 'Sin conexión: tu aporte quedó guardado y se enviará solo. Podés seguir aportando.'
                        : `Quedó en el acta${
                            misAportes.length > 0
                              ? ` — llevás ${misAportes.length} ${
                                  misAportes.length === 1 ? 'aporte' : 'aportes'
                                }`
                              : ''
                          }. Podés mandar otro.`}
                    </p>
                  )}
                </>
              ) : informativa ? (
                <MomentoInformativo />
              ) : (
                <CapturaNoSoportada
                  tipoCrudo={res.soportada ? null : res.tipoCrudo}
                />
              )}
            </div>

            {respuestas.length > 0 && (
              <RespuestasEnVivo respuestas={respuestas} misAportes={misAportes} />
            )}
            <LoQueYaAportaste aportes={misAportes} />
          </>
        )}

        {/* Evidencia rica (M2). Con captura de texto, la voz vive DENTRO del
            formulario (lleva la categoría elegida): acá solo foto/archivo. */}
        <LevantaEvidencia
          actividadId={actividad.id}
          transcripciones={transcripciones}
          soloAdjuntos={captura?.tipo === 'texto'}
        />

        <p className="mt-5 font-sans text-xs text-content-muted">
          {nombre ? `${nombre.split(' ')[0]}, tu` : 'Tu'} participación queda con tu
          nombre en el acta trazable de la sesión.
        </p>
      </main>
    </>
  );
}

/**
 * Momento informativo (cápsula, pausa, lectura): la herramienta declara
 * `capturas: []` a propósito. No es un error — el aporte pasa por la
 * proyección del salón, y la evidencia rica sigue disponible abajo.
 */
function MomentoInformativo() {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken">
        <Presentation className="h-9 w-9 text-content-muted" aria-hidden="true" />
      </span>
      <p role="status" className="mt-4 max-w-xs font-sans text-sm text-content-muted">
        Seguí la proyección del salón — esta parte no pide aportes desde tu
        teléfono.
      </p>
    </div>
  );
}

/**
 * El eco de lo tuyo: lista discreta de MIS aportes en la actividad en curso.
 * Con 0 no aparece; con más de 3 se colapsa (los más recientes primero).
 * Es eco, no protagonista: text-sm, muted, sin card.
 */
/**
 * Respuestas EN VIVO del facilitador a MIS aportes (Mago de Oz, Oleada 4):
 * el diálogo turno a turno queda registrado y citable; la ilusión del
 * «sistema que responde» vive acá, en el Bolsillo (la Pantalla no instruye).
 */
function RespuestasEnVivo({
  respuestas,
  misAportes,
}: {
  respuestas: Aporte[];
  misAportes: Aporte[];
}) {
  const mios = new Map(misAportes.map((a) => [a.id, a]));
  return (
    <section className="mt-5 flex flex-col gap-2">
      <h2 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <MessageCircle className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        Respuestas para vos
      </h2>
      {respuestas.map((r) => {
        const sobre = Array.isArray(r.payload.sobre)
          ? (r.payload.sobre as unknown[]).map(String)
          : [];
        const origen = sobre.map((id) => mios.get(id)).find(Boolean);
        return (
          <div
            key={r.id}
            className="rounded-md border border-border bg-surface-sunken px-3 py-2"
          >
            {origen && (
              <p className="font-sans text-xs text-content-muted">
                A tu mensaje: «{resumirAporte(origen)}»
              </p>
            )}
            <p className="mt-0.5 font-sans text-sm text-content">
              {typeof r.payload.valor === 'string' ? r.payload.valor : '(respuesta)'}
            </p>
          </div>
        );
      })}
    </section>
  );
}

function LoQueYaAportaste({ aportes }: { aportes: Aporte[] }) {
  const [abierto, setAbierto] = useState(false);
  if (aportes.length === 0) return null;

  const ordenados = [...aportes].sort((a, b) =>
    b.created_at.localeCompare(a.created_at)
  );
  const colapsable = ordenados.length > 3;
  const visibles = colapsable && !abierto ? ordenados.slice(0, 3) : ordenados;

  return (
    <section className="mt-5" aria-label="Lo que ya aportaste">
      <p className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
        Lo que ya aportaste
      </p>
      <ul className="mt-1.5 flex flex-col divide-y divide-border">
        {visibles.map((a) => (
          <li key={a.id} className="flex items-baseline justify-between gap-3 py-1.5">
            <p className="min-w-0 truncate font-sans text-sm text-content-muted">
              {resumirAporte(a)}
            </p>
            <time
              dateTime={a.created_at}
              className="shrink-0 font-sans text-xs text-content-muted"
            >
              {horaCorta(a.created_at)}
            </time>
          </li>
        ))}
      </ul>
      {colapsable && (
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="mt-1 flex min-h-touch items-center gap-1 font-sans text-sm text-content-muted hover:text-content"
        >
          {abierto ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
          {abierto ? 'Ver menos' : `Ver los ${ordenados.length}`}
        </button>
      )}
    </section>
  );
}

function horaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
}

function Confirmacion({
  otraVez,
  enCola,
  cuenta,
  onOtro,
}: {
  otraVez: string;
  enCola: boolean;
  cuenta: number;
  onOtro: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken">
        {enCola ? (
          <CloudUpload className="h-9 w-9 text-warning" aria-hidden="true" />
        ) : (
          <CheckCircle2 className="h-9 w-9 text-success" aria-hidden="true" />
        )}
      </span>
      <h1 className="mt-4 font-display text-xl font-semibold text-content">
        {enCola ? 'Tu aporte quedó guardado' : 'Tu aporte quedó en el acta'}
      </h1>
      <p className="mt-2 max-w-xs font-sans text-sm text-content-muted">
        {enCola
          ? 'Estás sin conexión ahora mismo. Guardamos tu aporte en tu teléfono y lo enviaremos solo, en cuanto vuelva la red. No lo pierdas de vista.'
          : 'Gracias por participar. Cuando el facilitador cierre esta actividad, tu aporte queda citado en el artefacto de la sesión.'}
      </p>
      {cuenta > 0 && (
        <p className="mt-2 font-sans text-sm text-content-muted">
          Llevás {cuenta} {cuenta === 1 ? 'aporte' : 'aportes'} en esta actividad.
        </p>
      )}
      <div className="mt-6 w-full max-w-xs">
        <Button
          variant="secondary"
          fullWidth
          onClick={onOtro}
          leadingIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
        >
          {otraVez}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------- Diario de campo ------------------------------ */

/**
 * Diario de campo (M2): la herramienta ENTRE-SESIONES. Se muestra solo cuando
 * no hay actividad en vivo. Reusa <ActividadAbierta> tal cual para la captura
 * y la evidencia; los 409 del backend (fase ya cerrada) salen por la misma
 * ruta de error humana de esa vista.
 */
function DiarioDeCampo({
  entradas,
  nombre,
  offsetMs,
  transcripciones,
}: {
  entradas: DiarioActividad[];
  nombre: string | null;
  offsetMs: number;
  transcripciones: Record<string, Transcripcion>;
}) {
  const qc = useQueryClient();
  const [seleccionId, setSeleccionId] = useState<string | null>(null);

  // Si la entrada elegida ya no está (cambió la fase, se cerró), caemos a la primera.
  const entrada = entradas.find((e) => e.id === seleccionId) ?? entradas[0];

  const detalle = useQuery({
    queryKey: sesionKeys.actividad(entrada.id),
    queryFn: () => sesionApi.actividad(entrada.id),
    refetchInterval: 20_000,
  });

  const act = detalle.data && detalle.data.estado === 'abierta' ? detalle.data : null;

  return (
    <>
      <div className="px-4 pt-2">
        <p className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
          <NotebookPen className="h-4 w-4" aria-hidden="true" />
          Tu diario de campo
        </p>
        <p className="mt-1 font-sans text-sm text-content-muted">
          Mientras no hay actividad en vivo, podés aportar a tu diario: lo que
          veas en territorio queda citado en el acta de la fase «
          {entrada.fase_nombre}».
        </p>

        {entradas.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Elegir diario">
            {entradas.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setSeleccionId(e.id)}
                aria-pressed={e.id === entrada.id}
                className={`min-h-touch rounded-md border px-3 font-sans text-sm ${
                  e.id === entrada.id
                    ? 'border-primary bg-surface-sunken font-semibold text-content'
                    : 'border-border text-content-muted hover:bg-surface-sunken'
                }`}
              >
                {e.herramienta_nombre}
              </button>
            ))}
          </div>
        )}
      </div>

      {act ? (
        <ActividadAbierta
          key={act.id}
          actividad={act}
          nombre={nombre}
          offsetMs={offsetMs}
          transcripciones={transcripciones}
          onEnviado={() => {
            qc.invalidateQueries({ queryKey: sesionKeys.actividad(act.id) });
            qc.invalidateQueries({ queryKey: ['aportes', act.id, 'mios'] });
          }}
        />
      ) : (
        <main className="flex-1 px-4 py-5">
          <p className="font-sans text-sm text-content-muted">
            {detalle.isLoading ? 'Abriendo tu diario…' : 'Este diario ya no recibe aportes.'}
          </p>
        </main>
      )}
    </>
  );
}

/* ------------------- Bienvenida (entrada por sala) ---------------------- */

/**
 * Confirmación breve al entrar por sala (?bienvenida=1): el participante ve a
 * DÓNDE está entrando antes del flujo normal. Contexto, no fricción: una sola
 * pantalla, un solo botón, y nunca vuelve a aparecer en la visita.
 */
function Bienvenida({
  proceso,
  cuando,
  cargando,
  nombre,
  onContinuar,
}: {
  proceso: ProcesoResumen | null;
  /** «sesión del 3 de agosto», si la sala del código la trae. */
  cuando: string;
  cargando: boolean;
  nombre: string | null;
  onContinuar: () => void;
}) {
  return (
    <div className="mx-auto flex min-h-screen max-w-pocket flex-col bg-surface-base">
      <header className="px-4 pb-2 pt-4">
        <img src="/brand/logopositivo.png" alt="ÁGORA" className="h-7 w-auto" />
      </header>
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-6 text-center">
        {cargando && !proceso ? (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-content-muted" aria-hidden="true" />
            <p className="mt-3 font-sans text-sm text-content-muted" role="status">
              Buscando tu proceso…
            </p>
          </>
        ) : (
          <>
            <p className="font-sans text-sm text-content-muted">Estás entrando a</p>
            <h1 className="mt-2 font-display text-2xl font-semibold text-content">
              {proceso?.nombre ?? 'Tu proceso participativo'}
            </h1>
            {(proceso?.org_nombre || cuando) && (
              <p className="mt-1 font-sans text-sm text-content-muted">
                {[proceso?.org_nombre, cuando].filter(Boolean).join(' · ')}
              </p>
            )}
            {nombre && (
              <p className="mt-4 font-sans text-sm text-content">
                Participás como <span className="font-semibold">{nombre}</span>.
              </p>
            )}
            <div className="mt-8 w-full max-w-xs">
              <Button
                variant="primary"
                fullWidth
                onClick={onContinuar}
                leadingIcon={<ArrowRight className="h-4 w-4" aria-hidden="true" />}
              >
                Continuar
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ------------------------ Mis participaciones --------------------------- */

const SESIONES_VIVAS = ['en_curso', 'abierta', 'activa', 'en_progreso'];

function badgeDeEstado(estado: string): BadgeVariant {
  if (SESIONES_VIVAS.includes(estado)) return 'success';
  if (estado === 'cerrada') return 'neutral';
  return 'info';
}

function fechaLegible(iso: string | null): string {
  if (!iso) return 'Fecha por definir';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Fecha por definir';
  return d.toLocaleDateString('es-GT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Historial del participante: sus procesos (con org y rol/grupo) y las
 * sesiones de cada uno. Vista de estado local (no ruta nueva); la query solo
 * corre al abrirla porque el componente solo se monta abierto.
 */
function MisParticipaciones({ onVolver }: { onVolver: () => void }) {
  const q = useQuery({
    queryKey: ['mis-participaciones'],
    queryFn: sesionApi.misParticipaciones,
    staleTime: 60_000,
  });

  return (
    <div className="mx-auto flex min-h-screen max-w-pocket flex-col bg-surface-base">
      <header className="flex items-center gap-2 px-4 pb-2 pt-4">
        <button
          type="button"
          onClick={onVolver}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
          aria-label="Volver"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="font-display text-lg font-semibold text-content">
          Mis participaciones
        </h1>
      </header>

      <main className="flex-1 px-4 py-4">
        {q.isLoading ? (
          <div className="flex flex-col gap-3">
            <EsqueletoTarjeta />
            <EsqueletoTarjeta />
          </div>
        ) : q.isError ? (
          <p role="alert" className="flex items-center gap-1.5 font-sans text-sm text-error">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            No pudimos traer tu historial. Probá de nuevo en un momento.
          </p>
        ) : !q.data || q.data.length === 0 ? (
          <p className="font-sans text-sm text-content-muted">
            Todavía no participás en ningún proceso. Cuando entrés a uno, acá
            queda tu historial.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {q.data.map((p) => (
              <Card key={p.proceso_id}>
                <h2 className="font-display text-base font-semibold text-content">
                  {p.proceso_nombre}
                </h2>
                <p className="mt-0.5 font-sans text-sm text-content-muted">{p.org_nombre}</p>
                <p className="mt-1 font-sans text-xs text-content-muted">
                  Participás como {p.rol}
                  {p.grupo_nombre ? ` · grupo ${p.grupo_nombre}` : ''}
                </p>

                {p.sesiones.length > 0 && (
                  <ul className="mt-3 flex flex-col divide-y divide-border">
                    {p.sesiones.map((s) => (
                      <li key={s.id} className="flex flex-col gap-1 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-sans text-sm font-medium text-content">
                            {fechaLegible(s.fecha)}
                          </p>
                          <Badge variant={badgeDeEstado(s.estado)}>
                            {s.estado.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <p className="font-sans text-xs text-content-muted">
                          {s.fase_nombre}
                          {s.mis_aportes > 0 &&
                            ` · aportaste ${s.mis_aportes} ${s.mis_aportes === 1 ? 'vez' : 'veces'}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/* ----------------------------- Sin sala --------------------------------- */

/**
 * No pertenecés a ningún proceso todavía: pedí el código y entrá.
 *
 * Es la pantalla que le faltaba a quien entra con Google SIN escanear el QR.
 * Antes, esa persona quedaba autenticada y sin membresía, y el Bolsillo la
 * mandaba al primer proceso activo de la org por defecto —una sala ajena—
 * donde el servidor le negaba todo en silencio. Ahora se le pide lo único que
 * hace falta: el código que está proyectado en la Pantalla del salón.
 */
function SinSala() {
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const unirme = useMutation({
    mutationFn: () => unirmeASala(codigo),
    onSuccess: (sala) => {
      setError(null);
      // La sala quedó fijada: que las consultas de proceso/sesión se rehagan.
      void qc.invalidateQueries({ queryKey: ['mis-participaciones'] });
      void qc.invalidateQueries({ queryKey: sesionKeys.procesos });
      void qc.invalidateQueries({ queryKey: sesionKeys.proceso(sala.proceso_id) });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.detail : 'No pudimos sumarte a esa sala.',
      );
    },
  });

  return (
    <main className="flex-1 px-4 py-6">
      <h1 className="font-display text-xl font-semibold text-content">
        Todavía no estás en ninguna sala
      </h1>
      <p className="mt-1 font-sans text-sm text-content-muted">
        Escribí el código que aparece en la Pantalla del salón —o volvé a
        escanear el QR— y quedás dentro del proceso para todas sus sesiones.
      </p>

      <form
        className="mt-6 flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (codigo.trim().length > 0) unirme.mutate();
        }}
      >
        <Input
          label="Código de la sala"
          value={codigo}
          onChange={(e) => {
            setCodigo(e.target.value);
            if (error) setError(null);
          }}
          error={error ?? undefined}
          hint="Se proyecta en la Pantalla, debajo del QR."
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          leadingIcon={<DoorOpen className="h-4 w-4" aria-hidden="true" />}
          required
        />
        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={unirme.isPending}
          disabled={codigo.trim().length === 0}
        >
          Entrar a la sala
        </Button>
      </form>

      <p className="mt-6 font-sans text-xs text-content-muted">
        ¿No tenés el código? Pedíselo a quien está facilitando: sin él no
        podemos saber a qué mesa sumarte.
      </p>
    </main>
  );
}

/* ----------------------------- Espera ---------------------------------- */

function Espera({ cargando, nombre }: { cargando: boolean; nombre: string | null }) {
  return (
    <main className="flex-1 px-4 py-6">
      <h1 className="font-display text-xl font-semibold text-content">
        Hola{nombre ? `, ${nombre.split(' ')[0]}` : ''}
      </h1>
      <p className="mt-1 font-sans text-sm text-content-muted">
        {cargando
          ? 'Buscando tu sesión…'
          : 'Cuando el facilitador abra un momento de participación, tu turno aparece acá. Todavía no hay nada abierto.'}
      </p>

      <div className="mt-6">
        <p className="mb-2 flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
          <Inbox className="h-4 w-4" aria-hidden="true" />
          Así se verá tu participación
        </p>
        <Card
          className="opacity-70"
          footer={<Badge variant="neutral">Ejemplo · aún no es real</Badge>}
        >
          <p className="flex items-start gap-2 text-sm text-content">
            <Droplets className="mt-0.5 h-4 w-4 shrink-0 text-cyan" aria-hidden="true" />
            «En la zona 3 el agua llega solo tres días por semana; deberíamos
            priorizar la red del casco urbano.»
          </p>
        </Card>
      </div>
    </main>
  );
}
